/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import axios from 'axios';
import { NavigateFunction } from 'react-router-dom';
import Cookies from 'js-cookie';
import env from '../utils/env';
import apigateway from '../utils/apigateway';
import { formatErrors, transformResponse } from '../utils/request';
import setCookie from '../utils/setCookie';
import { paths } from '../routes';
import setToLocalStorage from '../utils/setToLocalStorage';
import getFromLocalStorage from '../utils/getFromLocalStorage';
import getSubdomain from '../utils/getSubdomain';
import getAppURL from '../utils/getAppURL';
import {
  CashRegister,
  Country,
  DashboardStats,
  getStorePreferences,
  initialDashboardStats,
  PaymentPlan,
  StoreMeta,
  StorePreferences,
} from './storeContext';

axios.defaults.baseURL = env('POS_API_URL');

axios.defaults.headers.common.Accept = 'application/json';

// axios.defaults.withCredentials = true;

export type AuthToken = {
  userId: string;
  email: string;
  phoneNumber: string;
  token: string;
};

export type User = {
  surname: string;
  othernames: string;
  slug: string;
  email: string;
  emailVerifiedAt?: string;
  phoneNumber?: string;
  address?: string;
  otherAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  gender?: Gender;
  dob?: string;
  avatar?: string;
  isActive: boolean;
  notes?: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  userId: string;
  role: Role;
  defaultStore: Store;
  stores: Store[];
  selected: boolean;
};

export type Store = {
  id: number;
  name: string;
  displayName: string;
  businessType: string;
  industry: string;
  isPrimary: boolean;
  timezone: string;
  address: string;
  otherAddress: string;
  city: string;
  state: string;
  postalCode: string;
  website: string;
  email: string;
  phoneNumber: string;
  taxId: string;
  metadata: StoreMeta;
  createdAt: string;
  storeUrl: string;
  role: Role;
  storeAlert?: string;
  storeLogin?: string;
  cashRegisters: CashRegister[];
  paymentPlan: PaymentPlan;
  country: Country;
};

export type Gender = 'male' | 'female';

export type Role = 'Admin' | 'Manager' | 'Employee' | 'Webmaster';

type Alert = {
  message: string;
  type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO';
  show: boolean;
};

type AuthState = {
  user: User | null;
  status: 'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED';
  alert: Alert;
  modalState: 'CLOSED' | 'OPENED';
  storePreferences: StorePreferences | null;
  sessionExpired: { sessionMsg: string } | null;
  dashboardStats: DashboardStats;
};

type AuthDispatch = (action: AuthAction) => void;

type AuthAction =
  | { type: 'SET_AUTH_USER'; payload: User | null }
  | {
      type: 'SET_AUTH_STATUS';
      payload: 'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED';
    }
  | { type: 'SET_MODAL_STATE'; payload: 'CLOSED' | 'OPENED' }
  | { type: 'SHOW_ALERT'; payload: Alert }
  | { type: 'SET_STORE_PREFERENCES'; payload: StorePreferences | null }
  | { type: 'SET_SESSION_EXPIRED'; payload: { sessionMsg: string } | null }
  | { type: 'SET_DASHBOARD_STATS'; payload: DashboardStats };

const initialState: AuthState = {
  user: null,
  status: 'IDLE',
  alert: { message: '', type: 'INFO', show: false },
  modalState: 'CLOSED',
  storePreferences: null,
  sessionExpired: null,
  dashboardStats: initialDashboardStats,
};

const AuthContext = React.createContext<
  [state: AuthState, dispatch: AuthDispatch] | undefined
>(undefined);

AuthContext.displayName = 'AuthContext';

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_AUTH_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_AUTH_STATUS':
      return {
        ...state,
        status: action.payload,
      };
    case 'SHOW_ALERT':
      return {
        ...state,
        alert: action.payload,
      };
    case 'SET_MODAL_STATE':
      return {
        ...state,
        modalState: action.payload,
      };
    case 'SET_STORE_PREFERENCES':
      return {
        ...state,
        storePreferences: action.payload,
      };
    case 'SET_SESSION_EXPIRED':
      return {
        ...state,
        sessionExpired: action.payload,
      };
    case 'SET_DASHBOARD_STATS':
      return {
        ...state,
        dashboardStats: action.payload,
      };
    default:
      return state;
  }
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(authReducer, initialState);

  const value = React.useMemo(() => [state, dispatch], [state, dispatch]) as [
    state: AuthState,
    dispatch: AuthDispatch,
  ];

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`\`useAuth\` must be used within a AuthProvider`);
  }
  return context;
}

export async function syncAuthUser(dispatch: AuthDispatch) {
  dispatch({ type: 'SET_AUTH_STATUS', payload: 'LOADING' });

  const storedUser = JSON.parse(
    getFromLocalStorage('_pos_usrid') || '{}',
  ) as User;

  let storePreferences = null;

  if (storedUser.defaultStore) {
    try {
      storePreferences = await getStorePreferences(storedUser.defaultStore.id);
    } catch (error) {
      dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });

      const { message } = formatErrors(error);

      if (message === 'Unauthenticated.') {
        signOut(dispatch, 'DEVICE');
      } else {
        showAlert(dispatch, {
          show: true,
          message,
          type: 'ERROR',
        });
      }
    }
  }

  dispatch({ type: 'SET_AUTH_STATUS', payload: 'RESOLVED' });

  dispatch({ type: 'SET_AUTH_USER', payload: storedUser });

  dispatch({ type: 'SET_STORE_PREFERENCES', payload: storePreferences });
}

export function showAlert(
  dispatch: AuthDispatch,
  { show, type, message } = {} as Alert,
  { autoDismiss, time } = { autoDismiss: false, time: 0 },
) {
  dispatch({
    type: 'SHOW_ALERT',
    payload: { show, type, message },
  });

  if (autoDismiss) {
    setTimeout(
      () =>
        dispatch({
          type: 'SHOW_ALERT',
          payload: { show: false, type: 'INFO', message: '' },
        }),
      time,
    );
  }
}

export function signOut(
  dispatch: AuthDispatch,
  action: 'MANUAL' | 'SESSION' | 'DEVICE',
) {
  const subdomain = getSubdomain(window.location.origin);

  apigateway.post('/logout');

  if (action === 'DEVICE') {
    dispatch({
      type: 'SET_SESSION_EXPIRED',
      payload: {
        sessionMsg:
          "For security reasons, you've been logged out of this device. This may be due to session timeout or logging in to more than one device. To avoid this, only login with one device or browser at a time. Please login again to continue.",
      },
    });
    return;
  }

  if (action === 'SESSION') {
    dispatch({
      type: 'SET_SESSION_EXPIRED',
      payload: {
        sessionMsg:
          "For security reasons, your connection times out after you've been inactive for a while. Please login again to continue.",
      },
    });
    return;
  }

  Cookies.remove('_pos_tokid');

  Cookies.remove('_pos_store');

  Cookies.remove('_pos_possible_plan');

  localStorage.removeItem('_pos_sales');

  localStorage.removeItem('_pos_usrid');

  dispatch({ type: 'SET_AUTH_USER', payload: null });

  const host = window.location.origin.replace(`${subdomain}.`, 'www.');

  window.location.replace(`${host}${paths.LOGIN_URL_PATH}`);

  // window.location.reload();
}

export async function verifyToken(
  token: string,
  storeId: string,
  plan: string | null,
  cb: (response?: { type: 'RESOLVED' | 'REJECTED'; message: string }) => void,
) {
  try {
    const {
      data: {
        data: { user },
      },
    } = await getAuthUser(token, storeId);

    const store = user?.stores?.find(
      (fndStore: Store) => fndStore.id.toString() === storeId,
    );

    if (!user || !store) {
      cb({
        type: 'REJECTED',
        message: 'Account Error! Your store finder was not able to find store.',
      });
      return;
    }

    const authToken = {
      userId: user.userId,
      email: user.email,
      name: `${user.othernames} ${user.surname}`,
      phoneNumber: user.phoneNumber,
      token,
    } as AuthToken;

    const authUser: User = {
      ...user,
      defaultStore: store,
    };

    setToLocalStorage('_pos_usrid', JSON.stringify(authUser));

    await setCookie('_pos_tokid', JSON.stringify(authToken));

    await setCookie('_pos_store', JSON.stringify(store));

    if (plan && plan !== 'Free' && store.paymentPlan !== plan) {
      await setCookie('_pos_possible_plan', plan);
    }

    if (store?.storeAlert) {
      cb({
        type: 'RESOLVED',
        message: store?.storeAlert || '',
      });
      return;
    }

    cb();
  } catch (error) {
    cb({
      type: 'REJECTED',
      message: 'Account Error! Your store finder was not able to find store.',
    });
  }
}

export async function verifySubscription(
  reference: string,
  user: User,
  dispatch: AuthDispatch,
  cb: (response?: { type: 'RESOLVED' | 'REJECTED'; message: string }) => void,
) {
  const supportLink = env('POS_SUPPORT_URL') || '#';

  try {
    if (!user.defaultStore) {
      cb({
        type: 'REJECTED',
        message: `Oops. There was issue subscribing you to the selected plan. Please contact our support team! [Get Help](${supportLink})`,
      });
      return;
    }

    const response = (await apigateway.get('/verify-transaction', {
      reference,
      store: user.defaultStore.id,
    })) as any;

    if (!response || !response?.isValidTransaction) {
      cb({
        type: 'REJECTED',
        message: `Oops. There was issue subscribing you to the selected plan. Please contact our support team! [Get Help](${supportLink})`,
      });
      return;
    }

    Cookies.remove('_pos_possible_plan');

    // Update subscription field in state
    if (response.isValidTransaction) {
      const { user: getUser } = (await apigateway.get('/user', {
        store: user.defaultStore.id,
      })) as { user: User };

      const store = getUser?.stores?.find(
        (fndStore: Store) =>
          fndStore.id.toString() === user.defaultStore?.id.toString(),
      ) as Store;

      if (store) {
        const { paymentPlan } = store;

        const updatedStore = {
          ...user.defaultStore,
          paymentPlan,
        };

        const updatedUserPlan: User = {
          ...user,
          defaultStore: updatedStore,
        };

        setToLocalStorage('_pos_usrid', JSON.stringify(updatedUserPlan));

        await setCookie('_pos_store', JSON.stringify(updatedStore));

        dispatch({ type: 'SET_AUTH_USER', payload: updatedUserPlan });
      }

      cb({
        type: 'RESOLVED',
        message:
          'Your payment was processed successfully. Receipt of this transaction is available in your email.',
      });

      return;
    }

    cb();
  } catch (error) {
    cb({
      type: 'REJECTED',
      message: `Oops. There was issue subscribing you to the selected plan. Please contact our support team! [Get Help](${supportLink})`,
    });
  }
}

export async function signIn(
  dispatch: AuthDispatch,
  navigate: NavigateFunction,
  account: { email: string; password: string },
  destinationPath: string,
) {
  try {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'LOADING' });

    const response = (await apigateway.post('/login', {
      ...account,
      ...(getAppURL() && {
        callbackUrl: `${getAppURL()}${paths.STORE_FINDER_URL_PATH}/`,
      }),
      identity: 'user',
    })) as any;

    const onUser = {
      userId: response.user.userId,
      email: response.user.email,
      emailVerified: Boolean(response.user.emailVerifiedAt),
      name: `${response.user.othernames} ${response.user.surname}`,
      phoneNumber: response.user.phoneNumber,
      token: response.token,
    };

    dispatch({ type: 'SET_AUTH_STATUS', payload: 'RESOLVED' });

    navigate(paths.FIND_STORE_URL_PATH, {
      replace: true,
      state: { stores: response.user.stores, onUser, destinationPath },
    });
  } catch (error) {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });

    const { status } = formatErrors(error);

    let message = formatErrors(error)?.message;

    if (status === 500) {
      message = `Something unusual happened at our end (Unable to Login! ${message}). Please contact our support team.`;
    }

    showAlert(dispatch, {
      show: true,
      message,
      type: 'ERROR',
    });
  }
}

export async function createAccount(
  dispatch: AuthDispatch,
  navigate: NavigateFunction,
  account: {
    surname: string;
    othernames: string;
    email: string;
    phoneNumber: string;
    countryCode: string;
    storeName: string;
    password: string;
  },
  plan: string,
  cb: (registered: boolean) => void,
) {
  try {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'LOADING' });

    const response = (await apigateway.post('/create-store', {
      ...account,
      ...(getAppURL() && {
        callbackUrl: `${getAppURL()}${paths.STORE_FINDER_URL_PATH}/`,
      }),
      plan,
    })) as any;

    if (response === 'Store finder link (Magic Link) sent to your mail.') {
      dispatch({ type: 'SET_AUTH_STATUS', payload: 'RESOLVED' });
      cb(true);
      return;
    }

    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    cb(false);
    showAlert(dispatch, {
      show: true,
      message:
        "Couldn't create and register store. Please contact our support team!",
      type: 'ERROR',
    });
  } catch (error) {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    const { message } = formatErrors(error);
    showAlert(dispatch, {
      show: true,
      message,
      type: 'ERROR',
    });
  }
}

export async function sendResetPasswordLink(
  dispatch: AuthDispatch,
  account: { email: string },
  cb: (linkSent: boolean) => void,
) {
  try {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'LOADING' });

    const response = (await apigateway.post('/forgot-password', {
      ...account,
      ...(getAppURL() && {
        callbackUrl: `${getAppURL()}/reset-password/`,
      }),
      identity: 'user',
    })) as any;

    if (response === 'Reset password link sent to email.') {
      dispatch({ type: 'SET_AUTH_STATUS', payload: 'RESOLVED' });
      cb(true);
      return;
    }

    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    cb(false);
    showAlert(dispatch, {
      show: true,
      message:
        "Couldn't send instructions to email. Please contact our support team!",
      type: 'ERROR',
    });
  } catch (error) {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    const { message } = formatErrors(error);
    showAlert(dispatch, {
      show: true,
      message,
      type: 'ERROR',
    });
  }
}

export async function resetPassword(
  dispatch: AuthDispatch,
  navigate: NavigateFunction,
  account: { email: string; password: string; token: string },
) {
  try {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'LOADING' });

    const response = await axios({
      method: 'put',
      url: `reset-password`,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${account.token}`,
      },
      data: {
        ...account,
        identity: 'user',
      },
      transformResponse,
    });

    const {
      data: { data },
    } = response || {};

    if (data === 'Password has been updated successfully.') {
      dispatch({ type: 'SET_AUTH_STATUS', payload: 'RESOLVED' });
      navigate(paths.LOGIN_URL_PATH, {
        replace: true,
        state: {
          resetMsg:
            'Password has been updated successfully. Kindly login to continue.',
        },
      });
      return;
    }

    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    showAlert(dispatch, {
      show: true,
      message: "Couldn't reset password. Please contact our support team!",
      type: 'ERROR',
    });
  } catch (error) {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    const { message } = formatErrors(error);
    showAlert(dispatch, {
      show: true,
      message,
      type: 'ERROR',
    });
  }
}

export async function resendEmailAction(
  dispatch: AuthDispatch,
  payload: {
    email: string;
    callbackUrl: string;
    identity: 'user' | 'supplier' | 'customer';
    action: 'store-finder' | 'password';
    storeName?: string;
  },
  cb: (resent: boolean) => void,
) {
  try {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'LOADING' });

    const response = (await apigateway.post('/resend-email', payload)) as any;

    if (
      [
        'Reset password link sent to email.',
        'Store finder link (Magic Link) sent to your mail.',
      ].includes(response)
    ) {
      dispatch({ type: 'SET_AUTH_STATUS', payload: 'RESOLVED' });
      cb(true);
      showAlert(
        dispatch,
        {
          show: true,
          message: 'Email sent. Please check your inbox!',
          type: 'SUCCESS',
        },
        { autoDismiss: true, time: 3500 },
      );
      return;
    }

    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    cb(false);
    showAlert(dispatch, {
      show: true,
      message: "Couldn't resend  email. Please contact our support team!",
      type: 'ERROR',
    });
  } catch (error) {
    dispatch({ type: 'SET_AUTH_STATUS', payload: 'REJECTED' });
    const { message } = formatErrors(error);
    showAlert(dispatch, {
      show: true,
      message,
      type: 'ERROR',
    });
  }
}

function getAuthUser(token: string, storeId: string) {
  return axios({
    method: 'get',
    url: `/user?store=${storeId}`,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse,
  });
}

export { AuthProvider, useAuth };
